<script>
  import { onMount } from "svelte";
  import { Link, useLocation } from "svelte-navigator";
  import { derived } from "svelte/store";
  import Logo from "./logo.svelte";

  let showDesktop = true;
  let isMenuOpen = false;
  let isCollapsed = false;
  const desktopNavMinWidth = 1024;
  const expandedWidth = 252;
  const collapsedWidth = 76;
  const mobileBarHeight = 64;
  let resizeFrame;

  const location = useLocation();
  const pathname = derived(location, ($loc) => $loc.pathname);

  const toggleMenu = () => (isMenuOpen = !isMenuOpen);
  const closeMenu = () => (isMenuOpen = false);

  const toggleCollapse = () => {
    isCollapsed = !isCollapsed;
    localStorage.setItem("sidebarCollapsed", String(isCollapsed));
    dispatchLayout();
  };

  const handleOutsideClick = (event) => {
    const target = event.target;
    if (!target.closest(".mobile-menu") && !target.closest(".hamburger-btn")) {
      closeMenu();
    }
  };

  const iconPaths = {
    dashboard: "M3 10.5 12 3l9 7.5M5 10v10h5v-6h4v6h5V10",
    opportunities: "M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M9 12h6M9 16h4",
    customers: "M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M9.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
    projects: "M3 7h6l2 2h10v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z",
    teams: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
    marketing: "M3 11v2a2 2 0 0 0 2 2h2l4 4v-4h4l6-4V5l-6 4H5a2 2 0 0 0-2 2Z",
    statistics: "M4 19V9M10 19V5M16 19v-7M22 19H2",
    audit: "M8 3h8l3 3v15H5V6l3-3ZM8 7h8M8 11h8M8 15h5",
    logs: "M7 4h10M7 8h10M7 12h10M7 16h6M5 2h14v20H5V2Z",
    fields: "M4 7h16M7 7v10M17 7v10M4 17h16M10 12h4",
    settings: "M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5ZM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.65 1.65 0 0 0 15 19.4a1.65 1.65 0 0 0-1 .6 1.65 1.65 0 0 0-.38 1.06V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8.6 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-.6-1 1.65 1.65 0 0 0-1.06-.38H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 8.6a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-.6 1.65 1.65 0 0 0 .38-1.06V3a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15.4 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.4.18.75.43 1 .75.25.32.38.72.38 1.16V11a2 2 0 1 1 0 4h-.09A1.65 1.65 0 0 0 19.4 15Z",
  };

  const navItems = [
    { path: "/", label: "Dashboard", icon: "dashboard" },
    { path: "/opportunities", label: "Opportunities", icon: "opportunities" },
    { path: "/customers", label: "Customers", icon: "customers" },
    { path: "/projects", label: "Projects", icon: "projects" },
    { path: "/teams", label: "Teams", icon: "teams" },
    { path: "/marketing", label: "Marketing", icon: "marketing" },
    { path: "/statistics", label: "Statistics", icon: "statistics" },
    { path: "/auditLog", label: "Audit Log", icon: "audit" },
    { path: "/logs", label: "Logs", icon: "logs" },
  ];

  const configurationItems = [
    { path: "/settings", label: "Settings", icon: "settings" },
    { path: "/field-settings", label: "Field Settings", icon: "fields" },
  ];

  const dispatchLayout = () => {
    window.dispatchEvent(
      new CustomEvent("nav-layout", {
        detail: {
          isDesktop: showDesktop,
          sidebarWidth: showDesktop
            ? isCollapsed
              ? collapsedWidth
              : expandedWidth
            : 0,
          topOffset: showDesktop ? 0 : mobileBarHeight,
        },
      })
    );
  };

  const applyResize = () => {
    showDesktop = window.innerWidth >= desktopNavMinWidth;
    if (showDesktop) isMenuOpen = false;
    dispatchLayout();
  };

  const handleResize = () => {
    if (resizeFrame) cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null;
      applyResize();
    });
  };

  onMount(() => {
    isCollapsed = localStorage.getItem("sidebarCollapsed") === "true";
    applyResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
    };
  });

  $: sidebarWidth = isCollapsed ? collapsedWidth : expandedWidth;
</script>

<svelte:window on:click={handleOutsideClick} />

{#if showDesktop}
  <aside
    class="app-sidebar fixed top-0 left-0 z-50 flex h-screen flex-col overflow-hidden transition-[width] duration-200"
    style="width:{sidebarWidth}px; background:#15192a"
  >
    <!-- Logo -->
    <Link
      to="/"
      class="flex items-center gap-3 px-4 py-5 shrink-0 {isCollapsed ? 'justify-center px-2' : ''}"
    >
      <span class="inline-flex h-9 w-9 shrink-0 items-center justify-center">
        <Logo size={30} />
      </span>
      {#if !isCollapsed}
        <span class="font-display text-xl font-bold text-white">AcelinkHS</span>
      {/if}
    </Link>

    <!-- Nav items -->
    <nav class="flex-1 overflow-y-auto px-3 py-2 thin-scrollbar">
      {#if !isCollapsed}
        <div class="px-3 pb-2 nav-section-label font-semibold uppercase">
          Main
        </div>
      {/if}
      <ul class="space-y-1">
        {#each navItems as item}
          <li>
            <Link
              to={item.path}
              class="nav-item flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors {isCollapsed ? 'justify-center px-2' : ''} {$pathname === item.path ? 'nav-item-active' : ''}"
              title={isCollapsed ? item.label : undefined}
            >
              <svg class="nav-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <path d={iconPaths[item.icon]} stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {#if !isCollapsed}
                {item.label}
              {/if}
            </Link>
          </li>
        {/each}
      </ul>
      {#if !isCollapsed}
        <div class="px-3 pb-2 pt-8 nav-section-label font-semibold uppercase">
          Configuration
        </div>
      {/if}
      <ul class="space-y-1">
        {#each configurationItems as item}
          <li>
            <Link
              to={item.path}
              class="nav-item flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors {isCollapsed ? 'justify-center px-2' : ''} {$pathname === item.path ? 'nav-item-active' : ''}"
              title={isCollapsed ? item.label : undefined}
            >
              <svg class="nav-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <path d={iconPaths[item.icon]} stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {#if !isCollapsed}
                {item.label}
              {/if}
            </Link>
          </li>
        {/each}
      </ul>
    </nav>

    <!-- Collapse -->
    <div class="shrink-0 px-3 pb-4 pt-2">
      <button
        type="button"
        class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-white/50 transition-colors hover:bg-white/[0.06] hover:text-white {isCollapsed ? 'justify-center px-2' : ''}"
        on:click={toggleCollapse}
        title={isCollapsed ? "Expand" : "Collapse"}
      >
        <svg
          class="nav-chevron transition-transform {isCollapsed ? 'rotate-180' : ''}"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path d="m15 18-6-6 6-6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        {#if !isCollapsed}
          Collapse
        {/if}
      </button>
    </div>
  </aside>
{:else}
  <!-- Mobile top bar -->
  <nav
    class="mobile-topbar fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4"
    style="height:{mobileBarHeight}px; background:#15192a"
  >
    <Link to="/" class="flex items-center gap-3" on:click={closeMenu}>
      <span class="inline-flex h-9 w-9 items-center justify-center">
        <Logo size={28} />
      </span>
      <span class="font-display text-lg font-bold text-white">AcelinkHS</span>
    </Link>

    <button
      class="hamburger-btn rounded-lg p-2 transition-colors hover:bg-white/[0.08]"
      on:click|stopPropagation={toggleMenu}
      aria-label={isMenuOpen ? "Close menu" : "Open menu"}
      aria-expanded={isMenuOpen}
    >
      <svg class="mobile-nav-icon hamburger-icon {isMenuOpen ? 'open' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path class="icon-burger" d="M4 7h16M4 12h16M4 17h16" stroke-linecap="round" />
        <path class="icon-close" d="M6 6l12 12M18 6L6 18" stroke-linecap="round" />
      </svg>
    </button>
  </nav>

  {#if isMenuOpen}
    <div
      class="overlay fixed left-0 right-0 z-40"
      style="top:{mobileBarHeight}px; height:calc(100vh - {mobileBarHeight}px);"
      role="button"
      tabindex="0"
      on:click={closeMenu}
      on:keydown={(e) => e.key === "Enter" && closeMenu()}
      aria-label="Close menu"
    ></div>

    <div
      class="mobile-menu fixed left-0 z-50 w-72 max-w-[85vw] overflow-y-auto shadow-[12px_0_32px_rgba(0,0,0,0.55)]"
      style="top:{mobileBarHeight}px; height:calc(100vh - {mobileBarHeight}px); background:#15192a"
    >
      <ul class="space-y-1 px-3 py-4">
        <li class="px-3 pb-2 nav-section-label font-semibold uppercase">
          Main
        </li>
        {#each navItems as item}
          <li>
            <Link
              to={item.path}
              class="nav-item flex items-center gap-3 rounded-lg px-3 py-3 text-base font-medium transition-colors {$pathname === item.path ? 'nav-item-active' : ''}"
              on:click={closeMenu}
            >
              <svg class="mobile-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <path d={iconPaths[item.icon]} stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {item.label}
            </Link>
          </li>
        {/each}
        <li class="px-3 pb-2 pt-6 nav-section-label font-semibold uppercase">
          Configuration
        </li>
        {#each configurationItems as item}
          <li>
            <Link
              to={item.path}
              class="nav-item flex items-center gap-3 rounded-lg px-3 py-3 text-base font-medium transition-colors {$pathname === item.path ? 'nav-item-active' : ''}"
              on:click={closeMenu}
            >
              <svg class="mobile-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <path d={iconPaths[item.icon]} stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {item.label}
            </Link>
          </li>
        {/each}
      </ul>
    </div>
  {/if}
{/if}

<style>
  .overlay {
    background-color: rgba(5, 8, 16, 0.65);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
  }

  .app-sidebar {
    border-right: 1px solid rgba(255, 255, 255, 0.07);
  }

  .mobile-topbar {
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  }

  .mobile-menu {
    border-right: 1px solid rgba(255, 255, 255, 0.07);
  }

  .hamburger-btn {
    color: #ffffff;
  }

  .hamburger-icon path {
    transition:
      opacity 0.18s ease,
      transform 0.28s ease;
    transform-origin: center;
  }

  .hamburger-icon .icon-close {
    opacity: 0;
    transform: rotate(-90deg);
  }

  .hamburger-icon.open .icon-close {
    opacity: 1;
    transform: rotate(0deg);
  }

  .hamburger-icon.open .icon-burger {
    opacity: 0;
    transform: rotate(90deg);
  }

  .app-sidebar :global(svg) {
    max-width: none;
  }

  .app-sidebar :global(.nav-item),
  .mobile-menu :global(.nav-item) {
    color: #99a3b8;
  }

  .app-sidebar :global(.nav-item:hover),
  .mobile-menu :global(.nav-item:hover) {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
  }

  .app-sidebar :global(.nav-item-active),
  .mobile-menu :global(.nav-item-active),
  .app-sidebar :global(.nav-item-active:hover),
  .mobile-menu :global(.nav-item-active:hover) {
    color: #ffffff;
    background: linear-gradient(180deg, #3468f0, #2554dc);
    box-shadow: 0 6px 16px rgba(37, 99, 235, 0.35);
  }

  .nav-section-label,
  .mobile-menu :global(.nav-section-label) {
    color: #67718a;
    font-size: 0.68rem;
    letter-spacing: 1.3px;
  }

  .nav-item-icon {
    display: block;
    width: 18px !important;
    height: 18px !important;
    min-width: 18px !important;
    min-height: 18px !important;
  }

  .mobile-nav-icon {
    display: block;
    width: 20px !important;
    height: 20px !important;
    min-width: 20px !important;
    min-height: 20px !important;
  }

  .nav-chevron {
    display: block;
    width: 16px !important;
    height: 16px !important;
    min-width: 16px !important;
    min-height: 16px !important;
  }
</style>
